import { useMemo } from "react"
import { format, parseISO, startOfDay, subDays } from "date-fns"
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis } from "recharts"

import type { TagsGet, ThreadsGet } from "@productlane/api"

interface TagFeedbackChartProps {
	tag: TagsGet[number]
	threads: ThreadsGet
	color: string
}

interface TooltipProps {
	active?: boolean
	payload?: Array<{
		value: number
		dataKey?: string
	}>
	label?: string
}

const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
	if (active && payload && payload.length > 0 && label) {
		const count = payload[0]?.value ?? 0
		const date = parseISO(label)
		const formattedDate = format(date, "MMM d, yyyy")

		return (
			<div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-gray-700 dark:bg-gray-800">
				<p className="font-medium text-gray-900 dark:text-white">
					{formattedDate}
				</p>
				<p className="text-gray-600 dark:text-gray-400">
					{count} {count === 1 ? "request" : "requests"}
				</p>
			</div>
		)
	}
	return null
}

export const TagFeedbackChart = ({
	tag,
	threads,
	color,
}: TagFeedbackChartProps) => {
	const chartData = useMemo(() => {
		const tagThreads = threads.filter((thread) =>
			tag.feedbackIds.includes(thread.id),
		)

		if (tagThreads.length === 0) {
			return []
		}

		const now = new Date()
		const thirtyDaysAgo = startOfDay(subDays(now, 30))

		const dateMap = new Map<string, number>()

		for (let i = 0; i <= 30; i++) {
			const date = startOfDay(subDays(now, 30 - i))
			dateMap.set(format(date, "yyyy-MM-dd"), 0)
		}

		for (const thread of tagThreads) {
			const createdDate = parseISO(thread.createdAtIso)
			if (createdDate >= thirtyDaysAgo) {
				const dateKey = format(startOfDay(createdDate), "yyyy-MM-dd")
				dateMap.set(dateKey, (dateMap.get(dateKey) || 0) + 1)
			}
		}

		return Array.from(dateMap.entries())
			.map(([date, count]) => ({
				date,
				count,
			}))
			.sort((a, b) => a.date.localeCompare(b.date))
	}, [tag.feedbackIds, threads])

	if (chartData.length === 0) {
		return null
	}

	const maxCount = Math.max(...chartData.map((d) => d.count))

	if (maxCount === 0) {
		return null
	}

	return (
		<ResponsiveContainer width="100%" height={24}>
			<LineChart
				data={chartData}
				margin={{ top: 2, right: 0, left: 0, bottom: 2 }}
			>
				<XAxis dataKey="date" hide />
				<Tooltip content={<CustomTooltip />} />
				<Line
					type="monotone"
					dataKey="count"
					stroke={color}
					strokeWidth={1.75}
					dot={false}
					isAnimationActive={false}
				/>
			</LineChart>
		</ResponsiveContainer>
	)
}
